import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Play, LucideIconData } from 'lucide-angular';
import { IconComponent } from '../../../../shared/ui/icon/icon.component';

const YOUTUBE_ID_PATTERN =
  /(?:\/embed\/|\/shorts\/|[?&]v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/;

/**
 * Extrae el id de vídeo de YouTube de cualquier formato habitual —
 * `/embed/{id}`, `watch?v={id}`, `youtu.be/{id}`, `/shorts/{id}` — con o sin
 * query/fragment adicional (p. ej. el `?si=…` que añade el botón "Insertar"
 * de YouTube). `null` si no encuentra un id de 11 caracteres reconocible.
 *
 * OJO al leer el patrón: es DELIBERADAMENTE tolerante y NO valida el dominio
 * (`https://loquesea.com/x?v={id}` también casaría). Es inocuo porque de aquí
 * solo sale el ID: tanto el iframe como la carátula se reconstruyen desde cero
 * contra `youtube.com`/`img.youtube.com`, nunca concatenando sobre la URL de
 * entrada. La validación de dominio y formato es responsabilidad de la API
 * (`GameSectionController::youtubeEmbedRule`, que exige `youtube.com/embed/`
 * con id de 11 caracteres); aquí toleramos de más para no romper URLs pegadas
 * a mano en el backoffice.
 */
export function extractYouTubeId(url: string): string | null {
  return url.match(YOUTUBE_ID_PATTERN)?.[1] ?? null;
}

@Component({
  selector: 'app-game-video-embed',
  imports: [IconComponent],
  templateUrl: './game-video-embed.component.html',
  styleUrl: './game-video-embed.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GameVideoEmbedComponent {
  videoUrl = input.required<string>();
  caption = input<string>();

  protected readonly playIcon: LucideIconData = Play;
  protected isPlaying = signal(false);

  private sanitizer = inject(DomSanitizer);

  private readonly youtubeId = computed(() => {
    const id = extractYouTubeId(this.videoUrl());
    if (!id) {
      console.warn('[game-video-embed] no se pudo extraer el id de YouTube de', this.videoUrl());
    }
    return id;
  });

  protected thumbnailUrl = computed(() => {
    const id = this.youtubeId();
    return id ? `https://img.youtube.com/vi/${id}/maxresdefault.jpg` : null;
  });

  // Se construye limpia desde el id — nunca concatenando sobre `videoUrl()` —
  // para no arrastrar la query original (p. ej. `?si=…`) y acabar con un
  // `?autoplay=1` colgando de un segundo `?` que YouTube resuelve a su 404.
  protected safeVideoUrl = computed(() => {
    const id = this.youtubeId();
    if (!id) return null;
    return this.sanitizer.bypassSecurityTrustResourceUrl(
      `https://www.youtube.com/embed/${id}?autoplay=1`,
    );
  });

  protected play(): void {
    if (!this.youtubeId()) return;
    this.isPlaying.set(true);
  }
}
